import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Divider,
  Backdrop,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { Close, Route, Translate, Place, Category, Save, CloudUpload, RestartAlt } from "@mui/icons-material";
import { AdminPage, SectionTitle, GREEN, LIGHT, BORDER, primaryButtonSx } from "../admin/AdminPageLayout";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { create_travel, district_admin, district_get, languages, travel_admin, travel_types_admin, upload_image_all } from "../api/API";

function MainAddRoutes() {
  const token = localStorage.getItem("token");
  const name = localStorage.getItem("name");
  const [district, setDistrict] = useState([]);
  const [language, setLanguage] = useState([]);
  const [travelType, setTravelType] = useState([]);

  const [initialLoading, setInitialLoading] = useState(true);
  const [isDataReady, setIsDataReady] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // "success" | "error" | "warning" | "info"
  const [images, setImages] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    language: "",
    brandImage: "",
    infographicImage: "",
    did: "",
    ttid: "",
  });
  const [errorSnackbar, setErrorSnackbar] = useState({
    open: false,
    message: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      setInitialLoading(true);
      try {
        const [resDistrict, , resLanguage, resTravelType] = await Promise.all([
          axios.get(district_admin, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(travel_admin, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(languages, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(travel_types_admin, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setDistrict(resDistrict.data);
        setLanguage(resLanguage.data);
        setTravelType(resTravelType.data);
        setIsDataReady(true);
      } catch (err) {
        setErrorSnackbar({
          open: true,
          message: "โหลดข้อมูลล้มเหลว",
        });
      } finally {
        setInitialLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleReset = () => {
    setFormData({
      name: "",
      language: "",
      brandImage: null,
      infographicImage: null,
      district: "",
      routeType: "",
    });
    setImages([]);
  };

  const handleImageUpload = (e, type) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const newImages = files.map((file) => ({
        type,
        file,
        name: file.name,
        preview: URL.createObjectURL(file),
      }));

      const otherImages = images.filter((img) => img.type !== type);
      setImages([...otherImages, ...newImages]);
    }
  };

  const handleRemoveImage = (type, indexToRemove) => {
    // ลบเฉพาะภาพในกลุ่ม type นั้น ๆ
    const updatedImages = images?.filter((img, index) => {
      if (img.type !== type) return true; // เก็บไว้ถ้าคนละ type
      // ถ้า type เดียวกัน ให้ลบเฉพาะตัวที่ตรง index ของกลุ่มนั้น
      const sameTypeImages = images?.filter((i) => i.type === type);
      const targetImage = sameTypeImages[indexToRemove];
      return img !== targetImage;
    });

    setImages(updatedImages);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };


  const handleSubmit = async () => {
    if (!formData.name || !formData.language || !formData.did || !formData.ttid) {
      alert("กรุณากรอกข้อมูลให้ครบถ้วน");
      return;
    }

    if (!token || !name) {
      alert("ไม่พบ token หรือ name");
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const uid = decoded?.uid || decoded?.user_id || null;

      const payload = {
        ...formData,
        uid,
        img_main: '',
        img_infographic: '',
      };

      // console.log("Payload:", payload);

      // ส่ง payload (comment ไว้เพราะยังไม่ใช้งาน)
      const res = await axios.post(create_travel, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // console.log("res.data", res.data);

      if (res.status === 200 && res.data?.id) {
        // Upload image
        // const newRouteId = 1;
        setSnackbarMessage("✅ ข้อมูลถูกบันทึกเรียบร้อยแล้ว!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true); // <<== ต้องมี!
        setFormData({
          name: "",
          language: "",
          brandImage: "",
          infographicImage: "",
          ttid: "",
        });
        const newRouteId = res.data.id;
        const types = [...new Set(images.map((img) => img.type))];

        // 🌀 วนแต่ละ type แล้วอัปโหลด
        for (const type of types) {
          // เลือกรูปเฉพาะกลุ่มนั้น
          const imagesOfType = images.filter((img) => img.type === type);
          if (imagesOfType.length === 0) continue;

          const formDataUpload = new FormData();

          // เพิ่มไฟล์ทั้งหมดในกลุ่มนั้น
          imagesOfType.forEach((img) => {
            formDataUpload.append("files", img.file);
          });

          // ค่าอื่น ๆ ใส่แค่ครั้งเดียว
          formDataUpload.append("media_type", "image");
          formDataUpload.append("type", type); // ✅ ใช้ type ที่กำลังอัปโหลด
          formDataUpload.append("place_type", "route");
          formDataUpload.append("ref_id", newRouteId);
          formDataUpload.append("ref_name", "route");

          // console.log("Uploading images for type:", type, imagesOfType);

          const uploadRes = await axios.post(upload_image_all, formDataUpload, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          });
          // console.log("uploadRes:", uploadRes.data);

          // ✅ ตรวจ 200 หรือ 201
          if (uploadRes.status === 200 || uploadRes.status === 201) {
            console.log("✅ Upload success for type:", type);
          } else {
            console.warn("⚠️ Upload failed for type:", type);
          }
        }
        // ✅ แสดงข้อความสำเร็จครั้งเดียวหลังอัปโหลดครบทุกประเภท
        setSnackbarMessage("✅ ข้อมูลและรูปภาพถูกบันทึกเรียบร้อยแล้ว!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setImages([]);
        setFormData({
          name: "",
          language: "",
          brandImage: "",
          infographicImage: "",
          ttid: "",
        });
      } else {
        setSnackbarMessage("❌ เกิดข้อผิดพลาดในการบันทึก");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error("Error:", err);
      setSnackbarMessage("❌ เกิดข้อผิดพลาดในการบันทึก");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // พื้นที่อัปโหลดรูป + ตัวอย่างรูปของ type นั้น ๆ (ใช้ handler เดิม)
  const renderUpload = (type, label) => (
    <Box>
      <Button
        component="label"
        fullWidth
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          py: 3,
          border: `2px dashed ${BORDER}`,
          borderRadius: 3,
          bgcolor: LIGHT,
          color: GREEN,
          textTransform: "none",
          "&:hover": { bgcolor: "#e8f5e9", borderColor: GREEN },
        }}
      >
        <CloudUpload sx={{ fontSize: 40 }} />
        <Typography fontWeight={600}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">
          คลิกเพื่อเลือกรูปภาพ (เลือกได้หลายรูป)
        </Typography>
        <input
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => handleImageUpload(e, type)}
        />
      </Button>
      <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
        {images
          .filter((img) => img.type === type)
          .map((img, index) => (
            <Grid item xs={6} key={index}>
              <Box sx={{ position: "relative", borderRadius: 2, overflow: "hidden", border: `1px solid ${BORDER}`, bgcolor: "white" }}>
                <Box
                  component="img"
                  src={img.preview}
                  alt={`preview-${index}`}
                  sx={{ width: "100%", height: 120, objectFit: "cover", display: "block" }}
                />
                <Tooltip title="ลบรูปนี้">
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveImage(img.type, index)} // ✅ ส่ง type ด้วย
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      bgcolor: "rgba(255,255,255,0.9)",
                      color: "error.main",
                      "&:hover": { bgcolor: "white" },
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Typography variant="caption" noWrap sx={{ display: "block", px: 1, py: 0.5 }}>
                  {img.name}
                </Typography>
              </Box>
            </Grid>
          ))}
      </Grid>
    </Box>
  );

  return (
    <Box>
      {!isDataReady ? (
        <Box textAlign="center" mt={5}>
          <CircularProgress sx={{ color: GREEN }} />
          <Typography mt={2}>กำลังโหลดข้อมูล...</Typography>
        </Box>
      ) : (
        <>
          <AdminPage
            icon={<Route fontSize="large" />}
            title="เพิ่มชื่อ Route"
            subtitle="สร้างเส้นทางท่องเที่ยวใหม่ พร้อมรูปแบนเนอร์และอินโฟกราฟฟิก"
            actions={
              <>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleReset}
                  startIcon={<RestartAlt />}
                  sx={{ borderRadius: 3, width: { xs: "100%", sm: "auto" }, borderColor: "grey.400", color: "text.secondary" }}
                >
                  รีเซต
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSubmit}
                  startIcon={<Save />}
                  sx={primaryButtonSx}
                >
                  บันทึกข้อมูล
                </Button>
              </>
            }
          >
            <SectionTitle step={1} title="ข้อมูลเส้นทาง" />
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  label="ชื่อเส้นทาง"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Route sx={{ color: GREEN }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>ใช้สำหรับภาษา</InputLabel>
                  <Select
                    name="language"
                    value={formData.language}
                    onChange={handleChange}
                    label="ใช้สำหรับภาษา"
                    startAdornment={
                      <InputAdornment position="start">
                        <Translate sx={{ color: GREEN }} />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="">
                      <em>-- เลือกภาษา --</em>
                    </MenuItem>
                    {language?.length > 0 &&
                      language?.map((d) => (
                        <MenuItem key={d.lid} value={d.lid}>
                          {d.local}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <SectionTitle step={2} title="พื้นที่และรูปแบบเส้นทาง" />
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>อำเภอ</InputLabel>
                  <Select
                    name="did"
                    value={formData.did}
                    onChange={handleChange}
                    label="อำเภอ"
                    disabled={initialLoading}
                    startAdornment={
                      <InputAdornment position="start">
                        <Place sx={{ color: GREEN }} />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="">
                      <em>-- เลือกอำเภอ --</em>
                    </MenuItem>
                    {district?.length > 0 &&
                      district?.map((d) => (
                        <MenuItem key={d.did} value={d.did}>
                          {d.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>รูปแบบเส้นทาง</InputLabel>
                  <Select
                    name="ttid"
                    value={formData.ttid}
                    onChange={handleChange}
                    label="รูปแบบเส้นทาง"
                    startAdornment={
                      <InputAdornment position="start">
                        <Category sx={{ color: GREEN }} />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="">
                      <em>-- เลือกเส้นทาง --</em>
                    </MenuItem>
                    {travelType.length > 0 &&
                      travelType?.map((d) => (
                        <MenuItem key={d.ttid} value={d.ttid}>
                          {d.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <SectionTitle step={3} title="รูปภาพ" />
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                {renderUpload("banner_route", "รูปแบนเนอร์เส้นทาง")}
              </Grid>
              <Grid item xs={12} sm={6}>
                {renderUpload("info_route", "รูปอินโฟกราฟฟิก")}
              </Grid>
            </Grid>
          </AdminPage>

          {/* // JSX ส่วนแสดง error Snackbar */}
          <Snackbar
            open={errorSnackbar.open}
            autoHideDuration={3000}
            onClose={() => setErrorSnackbar({ open: false, message: "" })}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              severity="error"
              variant="filled"
              sx={{ fontWeight: "bold" }}
            >
              {errorSnackbar.message}
            </Alert>
          </Snackbar>
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={4000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity={snackbarSeverity}
              sx={{ width: "100%" }}
              variant="filled"
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
          {/* Loading Overlay */}
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={initialLoading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </>
      )}
    </Box>
  );
}

export default MainAddRoutes;
